import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import * as customerService from '../services/customerService';
import type { CustomerWithCounts } from '../services/customerService';

/**
 * Customer Context
 *
 * Tracks the active Customer scope for the entire app. Visible Customers are
 * fetched from the server (which already filters by user role + Customer_Internal_Members).
 * The user picks one via the global CustomerSelector in the header; switching
 * causes downstream pages to refetch their data scoped to the new Customer.
 *
 * For role='customer' users the active Customer is locked to their assigned one
 * and the selector is non-interactive.
 */

interface CustomerContextValue {
  availableCustomers: CustomerWithCounts[];
  activeCustomer: CustomerWithCounts | null;
  setActiveCustomerId: (id: string) => void;
  refresh: () => Promise<void>;
  isLoading: boolean;
  /** True when current user role makes the selector read-only (`customer`). */
  isLocked: boolean;
}

const CustomerContext = createContext<CustomerContextValue | undefined>(undefined);

const STORAGE_KEY = 'nivoon_active_customer_id';

interface ProviderProps { children: ReactNode; }

export function CustomerProvider({ children }: ProviderProps): React.ReactElement {
  const { token, user, isAuthenticated } = useAuth();
  const [availableCustomers, setAvailableCustomers] = useState<CustomerWithCounts[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));
  const [isLoading, setIsLoading] = useState(false);

  const isLocked = user?.role === 'customer';

  const refresh = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    setIsLoading(true);
    try {
      const customers = await customerService.listCustomers(token);
      setAvailableCustomers(customers);

      // Pick a sensible active Customer if the stored one is no longer accessible
      setActiveId(prev => {
        if (prev && customers.some(c => c.id === prev)) return prev;
        // Default: prefer the Internal Customer if present, else first by name
        const internal = customers.find(c => c.is_internal);
        const next = internal?.id || customers[0]?.id || null;
        if (next) localStorage.setItem(STORAGE_KEY, next);
        return next;
      });
    } catch (err) {
      console.error('CustomerContext refresh failed:', err);
      setAvailableCustomers([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && token) {
      refresh();
    } else {
      setAvailableCustomers([]);
      setActiveId(null);
    }
  }, [isAuthenticated, token, refresh]);

  const setActiveCustomerId = useCallback((id: string) => {
    if (isLocked) return; // customer-role users cannot switch
    setActiveId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, [isLocked]);

  const activeCustomer = availableCustomers.find(c => c.id === activeId) || null;

  return (
    <CustomerContext.Provider
      value={{ availableCustomers, activeCustomer, setActiveCustomerId, refresh, isLoading, isLocked }}
    >
      {children}
    </CustomerContext.Provider>
  );
}

export function useCustomer(): CustomerContextValue {
  const ctx = useContext(CustomerContext);
  if (!ctx) throw new Error('useCustomer must be used within CustomerProvider');
  return ctx;
}
